import { Color } from './java/Color';
import { Font, FontMetrics, Graphics2D, RenderingHints } from './java/Graphics2D';
import { Rectangle } from './java/Geom';
import { Random } from './java/Random';
import { GameObject } from './GameObject';
import { Handler } from './Handler';
import { ID } from './ID';
import { Game } from './Game';
import { EnemyBossBullet } from './EnemyBossBullet';
import { PageRenderer } from './PageRenderer';

/**
 * Splitter Boss — on defeat, splits into smaller copies that each split once more.
 * Generation 0 = main boss (large), gen 1 = halves, gen 2 = quarters (final).
 * The boss is defeated when all fragments are gone.
 * Bounces around the arena. Each generation is faster and smaller.
 */
export class BossSplitter extends GameObject {

  private static readonly BASE_SIZE = 88;
  private static readonly R = 14;

  private handler: Handler;
  private r: Random = new Random();
  private pulsePhase: number;
  private readonly boundsRect: Rectangle = new Rectangle();
  private static readonly EMPTY_BOUNDS: Rectangle = new Rectangle(0, 0, 0, 0);

  private generation: number; // 0 = main, 1 = half, 2 = quarter
  private size: number;
  private maxHp: number;
  private hp: number;
  private defeated = false;
  private deathTimer = 0;
  private hasSpawned = false; // prevent double-split

  // Entry animation (gen 0 only)
  private entryTimer: number;
  private warmupTimer = 30;

  // Colors — green/teal theme to distinguish from red EnemyBoss
  private static readonly FILL_G0 = new Color(72, 199, 142);
  private static readonly FILL_G1 = new Color(90, 210, 155);
  private static readonly FILL_G2 = new Color(110, 220, 170);
  private static readonly BAR_BG = new Color(22, 30, 44);
  private static readonly BAR_BORDER = new Color(40, 52, 70);
  private static readonly FONT_BOSS = new Font('Arial', Font.BOLD, 14);
  private static readonly FONT_PHASE = new Font('Arial', Font.BOLD, 11);

  constructor(x: number, y: number, id: ID, handler: Handler, generation: number) {
    super(x, y, id);
    this.handler = handler;
    this.generation = generation;
    this.pulsePhase = this.r.nextFloat() * 6.28;

    // Size shrinks per generation
    this.size = generation === 0
      ? BossSplitter.BASE_SIZE
      : generation === 1
        ? Math.trunc(BossSplitter.BASE_SIZE / 2)
        : Math.trunc(BossSplitter.BASE_SIZE / 3);

    // HP per generation
    this.maxHp = generation === 0 ? 900 : generation === 1 ? 300 : 100;
    this.hp = this.maxHp;

    // Speed increases per generation
    const speed = generation === 0 ? 3 : generation === 1 ? 5 : 7;
    this.velX = (this.r.nextBoolean() ? speed : -speed) * (0.8 + this.r.nextFloat() * 0.4);
    this.velY = (this.r.nextBoolean() ? speed : -speed) * (0.8 + this.r.nextFloat() * 0.4);

    this.entryTimer = generation === 0 ? 100 : 0;
    if (generation === 0) {
      this.velX = 0;
      this.velY = 2;
    }
  }

  setMaxHp(hp: number): void {
    // Scale all generations proportionally
    const ratio = hp / 900;
    if (this.generation === 0) { this.maxHp = hp; }
    else if (this.generation === 1) { this.maxHp = 300 * ratio; }
    else { this.maxHp = 100 * ratio; }
    this.hp = this.maxHp;
  }

  isDefeated(): boolean {
    if (this.generation < 2) return false; // gen 0/1 split, never "fully" defeated
    return this.defeated && this.deathTimer > 30;
  }

  override takeDamage(dmg: number): boolean {
    if (this.defeated) return false;
    this.hp -= dmg;
    if (this.hp < 0) this.hp = 0;
    return true;
  }

  /** Check if this specific fragment is dead (used by Spawn to detect all fragments gone) */
  isFragmentDead(): boolean {
    return this.defeated && this.deathTimer > 30;
  }

  getGeneration(): number { return this.generation; }

  getHpPercent(): number { return this.hp / this.maxHp; }

  getBounds(): Rectangle {
    if (this.defeated || this.entryTimer > 0) return BossSplitter.EMPTY_BOUNDS;
    this.boundsRect.x = Math.trunc(this.x);
    this.boundsRect.y = Math.trunc(this.y);
    this.boundsRect.width = this.size;
    this.boundsRect.height = this.size;
    return this.boundsRect;
  }

  tick(): void {
    if (this.defeated) {
      this.deathTimer++;
      if (!this.hasSpawned && this.generation < 2) {
        this.hasSpawned = true;
        this.split();
      }
      return;
    }

    this.pulsePhase += 0.06;

    // Entry animation (gen 0 only)
    if (this.entryTimer > 0) {
      this.x += this.velX;
      this.y += this.velY;
      this.entryTimer--;
      if (this.entryTimer <= 0) { this.velY = 0; }
      return;
    }
    if (this.warmupTimer > 0) {
      this.warmupTimer--;
      if (this.warmupTimer <= 0 && this.generation === 0) {
        const speed = 3;
        this.velX = (this.r.nextBoolean() ? speed : -speed);
        this.velY = (this.r.nextBoolean() ? speed : -speed) * (0.6 + this.r.nextFloat() * 0.4);
      }
      return;
    }

    this.x += this.velX;
    this.y += this.velY;

    // HP drains over time
    let drainRate = 0.6 + (1 - this.hp / this.maxHp) * 0.3;
    if (this.generation === 1) drainRate *= 1.3;
    if (this.generation === 2) drainRate *= 1.8;
    if (Game.combatMode) drainRate *= 0.35;
    this.hp -= drainRate;

    if (this.hp <= 0) {
      this.hp = 0;
      this.defeated = true;
      if (this.generation === 2) {
        Game.triggerBulletCascade(
          this.x + Math.trunc(this.size / 2),
          this.y + Math.trunc(this.size / 2),
          this.handler,
        );
      }
      return;
    }

    // Bounce off walls
    if (this.y <= 0 || this.y >= Game.HEIGHT - this.size) {
      Game.wallHit(this.x + Math.trunc(this.size / 2), this.y <= 0 ? 0 : Game.HEIGHT, this.y <= 0 ? 0 : 1,
        this.getFill().getRed(), this.getFill().getGreen(), this.getFill().getBlue());
      this.velY *= -1;
      if (this.y <= 0) this.y = 0;
      if (this.y >= Game.HEIGHT - this.size) this.y = Game.HEIGHT - this.size;
    }
    if (this.x <= 0 || this.x >= Game.WIDTH - this.size) {
      Game.wallHit(this.x <= 0 ? 0 : Game.WIDTH, this.y + Math.trunc(this.size / 2), this.x <= 0 ? 2 : 3,
        this.getFill().getRed(), this.getFill().getGreen(), this.getFill().getBlue());
      this.velX *= -1;
      if (this.x <= 0) this.x = 0;
      if (this.x >= Game.WIDTH - this.size) this.x = Game.WIDTH - this.size;
    }

    // Shoot occasional bullets (gen 0 and 1 only)
    if (this.generation <= 1 && this.r.nextInt(this.generation === 0 ? 80 : 120) === 0) {
      const angle = this.r.nextFloat() * (Math.PI * 2);
      const bspeed = 3 + this.r.nextFloat() * 2;
      this.handler.addObject(new EnemyBossBullet(
        Math.trunc(this.x + Math.trunc(this.size / 2)), Math.trunc(this.y + Math.trunc(this.size / 2)),
        ID.BasicEnemy, this.handler,
        Math.cos(angle) * bspeed, Math.sin(angle) * bspeed));
    }
  }

  private split(): void {
    const nextGen = this.generation + 1;
    const nextSize = nextGen === 1
      ? Math.trunc(BossSplitter.BASE_SIZE / 2)
      : Math.trunc(BossSplitter.BASE_SIZE / 3);
    const count = nextGen === 1 ? 2 : 3;

    const cx = this.x + Math.trunc(this.size / 2);
    const cy = this.y + Math.trunc(this.size / 2);

    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i / count) + this.r.nextFloat() * 0.5;
      const offset = this.size * 0.4;
      let sx = Math.trunc(cx + Math.cos(angle) * offset - Math.trunc(nextSize / 2));
      let sy = Math.trunc(cy + Math.sin(angle) * offset - Math.trunc(nextSize / 2));
      sx = Math.max(0, Math.min(Game.WIDTH - nextSize, sx));
      sy = Math.max(0, Math.min(Game.HEIGHT - nextSize, sy));

      const fragment = new BossSplitter(sx, sy, ID.EnemyBoss, this.handler, nextGen);
      // Scale HP proportionally
      const ratio = this.maxHp / 900;
      fragment.setMaxHp(nextGen === 1 ? 300 * ratio : 100 * ratio);
      this.handler.addObject(fragment);
    }
  }

  private getFill(): Color {
    return this.generation === 0
      ? BossSplitter.FILL_G0
      : this.generation === 1 ? BossSplitter.FILL_G1 : BossSplitter.FILL_G2;
  }

  render(g: Graphics2D): void {
    const g2 = g;
    g2.setRenderingHint(RenderingHints.KEY_ANTIALIASING, RenderingHints.VALUE_ANTIALIAS_ON);
    const ix = Math.trunc(this.x), iy = Math.trunc(this.y);

    if (this.defeated) {
      const t = Math.min(this.deathTimer / 30, 1);
      const shrink = Math.trunc(t * this.size / 2);
      const alpha = Math.trunc((1 - t) * 255);
      if (alpha > 0) {
        g2.setColor(new Color(255, 255, 255, alpha));
        g2.fillOval(ix + shrink, iy + shrink, this.size - shrink * 2, this.size - shrink * 2);
      }
      return;
    }

    const fill = this.getFill();
    const pulse = Math.sin(this.pulsePhase) * 0.5 + 0.5;

    // Glow
    const glowSize = 6 + Math.trunc(3 * pulse);
    g2.setColor(new Color(fill.getRed(), fill.getGreen(), fill.getBlue(), 15 + Math.trunc(12 * pulse)));
    g2.fillOval(ix - glowSize, iy - glowSize, this.size + glowSize * 2, this.size + glowSize * 2);

    // Main body — hexagonal shape
    const cx = ix + Math.trunc(this.size / 2), cy = iy + Math.trunc(this.size / 2);
    const hr = Math.trunc(this.size / 2);
    const hx: number[] = new Array<number>(6).fill(0);
    const hy: number[] = new Array<number>(6).fill(0);
    for (let i = 0; i < 6; i++) {
      const a = this.pulsePhase * 0.3 + i * (Math.PI / 3);
      hx[i] = cx + Math.trunc(Math.cos(a) * hr);
      hy[i] = cy + Math.trunc(Math.sin(a) * hr);
    }
    g2.setColor(fill);
    g2.fillPolygon(hx, hy, 6);
    g2.setColor(new Color(fill.getRed(), fill.getGreen(), fill.getBlue(), 100 + Math.trunc(60 * pulse)));
    g2.drawPolygon(hx, hy, 6);

    // Inner split lines — hint at the splitting mechanic
    if (this.generation < 2) {
      g2.setColor(new Color(255, 255, 255, 30 + Math.trunc(20 * pulse)));
      const lines = this.generation === 0 ? 2 : 3;
      for (let i = 0; i < lines; i++) {
        const a = this.pulsePhase * 0.2 + i * (Math.PI / lines);
        const dx = Math.trunc(Math.cos(a) * hr * 0.7);
        const dy = Math.trunc(Math.sin(a) * hr * 0.7);
        g2.drawLine(cx - dx, cy - dy, cx + dx, cy + dy);
      }
    }

    // Gen 0 draws boss bar
    if (this.generation === 0) {
      this.renderBossBar(g2);
    }
  }

  private renderBossBar(g: Graphics2D): void {
    const barW = 400;
    const barH = 10;
    const barX = Math.trunc((Game.WIDTH - barW) / 2);
    const barY = Game.HEIGHT - 40;

    // Aggregate HP across all living fragments
    let totalHp = 0, totalMax = 0;
    for (let i = 0; i < this.handler.getObjects().length; i++) {
      const obj: GameObject = this.handler.getObjects()[i];
      if (obj instanceof BossSplitter) {
        const bs: BossSplitter = obj;
        if (!bs.defeated) {
          totalHp += bs.hp;
          totalMax += bs.maxHp;
        }
      }
    }
    // Include self if still alive
    if (!this.defeated) {
      // Already counted in loop above
    }
    if (totalMax <= 0) return;
    const pct = totalHp / totalMax;

    g.setColor(BossSplitter.BAR_BG);
    g.fillRoundRect(barX, barY, barW, barH, 5, 5);

    const fill = this.getFill();
    const fillW = Math.trunc(barW * pct);
    if (fillW > 0) {
      g.setColor(fill);
      g.fillRoundRect(barX, barY, fillW, barH, 5, 5);
    }

    g.setColor(BossSplitter.BAR_BORDER);
    g.drawRoundRect(barX, barY, barW, barH, 5, 5);

    g.setFont(BossSplitter.FONT_BOSS);
    g.setColor(PageRenderer.TEXT_SEC);
    const fm: FontMetrics = g.getFontMetrics();
    g.drawString('SPLITTER', barX - fm.stringWidth('SPLITTER') - 10, barY + 9);

    g.setFont(BossSplitter.FONT_PHASE);
    g.setColor(fill);
    // Count living fragments
    let fragments = 0;
    for (let i = 0; i < this.handler.getObjects().length; i++) {
      const obj: GameObject = this.handler.getObjects()[i];
      if (obj instanceof BossSplitter && !obj.defeated) fragments++;
    }
    g.drawString(fragments + ' alive', barX + barW + 10, barY + 9);
  }
}
